import React, { useMemo } from 'react';
import CustomDialog from 'components/CustomDialog/CustomDialog';
import { translate } from 'components/Translation';
import styles from 'assets/jss/material-dashboard-pro-react/views/accounts/accountDetailsSubpageStyle';
import makeStyles from '@material-ui/core/styles/makeStyles';
import FileUpload from 'components/CustomUpload/FileUpload';
import { Caption, CaptionBold, CaptionLight } from 'components/Typography/Typography';
import CustomButton from 'components/CustomButtons/Button';
import { Delete } from '@material-ui/icons';
import Circle from '@material-ui/icons/CheckCircleOutlined';
import CustomInput from 'components/CustomInput/CustomInput';
import classNames from 'classnames';
import { AccountsService } from 'services/accounts/accounts';
import invalidateCache from 'utils/api/invalidateCache';
import { setToast } from 'redux/modules/toast/actions';
import { dispatch } from 'redux/store';

const getValue = (event: React.FormEvent<HTMLInputElement>) => (event.target as HTMLInputElement).value;

interface IOrganizationTaxIdDialogProps {
	isDialogOpen: boolean;
	hideDialog: () => void;
	accountId: string;
	taxId?: string;
}

const OrganizationTaxIdDialog = ({
	hideDialog,
	isDialogOpen,
	taxId: defaultTaxId = '',
	accountId,
}: IOrganizationTaxIdDialogProps) => {
	// @ts-ignore
	const useStyles = makeStyles(styles);
	const classes = useStyles();
	const [fileName, setFileName] = React.useState<any>();
	const [fileBase64, setFileBase64] = React.useState<any>();
	const [taxId, setTaxId] = React.useState<string>(defaultTaxId);
	const [loading, setLoading] = React.useState<boolean>(false);
	const [errorMessages, setErrorMessages] = React.useState<any>({});

	const saveButtonDisabled: boolean = useMemo(() => {
		return !taxId || !fileBase64 || !fileName;
	}, [taxId, fileName, fileBase64]);

	const handleSubmit = () => {
		if (fileBase64 && taxId) {
			setLoading(true);
			const taxData = {
				taxId,
				accountId,
				taxIdCertificateFileName: fileName,
				taxIdCertificateFile: fileBase64,
			};
			setErrorMessages({});
			AccountsService.updateOrganizationTaxId(taxData).subscribe(
				({ response }) => {
					setLoading(false);
					if (response === 'success') {
						hideDialog();
						dispatch(
							setToast({
								label: translate('organization.details.tax.upload.success.organization'),
								actionType: translate(
									`organization.details.tax.upload.success.${defaultTaxId ? 'edited' : 'added'}`
								),
							})
						);
						invalidateCache(['getCorpAccountDetails']);
					} else {
						dispatch(
							setToast({ error: true, label: translate('error.occured'), actionType: translate('error') })
						);
					}
				},
				() => {
					setLoading(false);
				}
			);
		} else {
			setErrorMessages(() => ({
				fileBase64: !fileBase64 ? translate('organization.details.tax.upload.file.error') : undefined,
				taxId: !taxId ? translate('organization.details.tax.upload.id.error') : undefined,
			}));
		}
	};

	return (
		<CustomDialog
			isOpen={isDialogOpen}
			loading={loading}
			header={translate(
				defaultTaxId ? 'organization.details.tax.upload.edit' : 'organization.details.tax.upload.add'
			)}
			dialogBody={
				<div className={classes.taxDialogContainer}>
					<CustomInput
						className={classes.taxDialogInput}
						formControlProps={{
							fullWidth: true,
						}}
						id="taxId-input"
						labelText={translate('organization.details.tax.id')}
						error={errorMessages?.taxId}
						inputProps={{
							value: taxId,
							onChange: (event: React.FormEvent<HTMLInputElement>) => {
								const val = getValue(event);
								setErrorMessages((msg: any) => ({
									...msg,
									taxId: !val,
								}));
								setTaxId(val);
							},
						}}
					/>
					{fileName ? (
						<div className={classNames(classes.upload, classes.file)}>
							<Caption color="disabled-light">
								{translate(`organization.details.tax.id.certificate`)}
							</Caption>
							<div className={classes.fileNameContainer}>
								<Circle className={classes.success} />
								<div className={classes.filename}>
									<CaptionBold inline>{fileName}</CaptionBold>
								</div>
								<CustomButton
									color="tertiary-light"
									className={classes.deleteIcon}
									justIcon
									size="lg"
									onClick={() => setFileName(() => undefined)}
								>
									<Delete />
								</CustomButton>
							</div>
						</div>
					) : (
						<div>
							<FileUpload
								iconPosition="right"
								size="sm"
								text={translate('organization.details.tax.upload.certificate')}
								onFileChange={(file) => setFileBase64(file)}
								onMetadataChange={(data) => setFileName(data.name)}
								errorMessage={errorMessages?.fileBase64}
								acceptedExtensions={['application/pdf', 'image/jpeg', 'image/png']}
								insideError
							/>
							<CaptionLight>{translate('organization.details.tax.upload.file.types')}</CaptionLight>
						</div>
					)}
					<div className={classes.taxIdDescription}>
						<CaptionBold>{translate('organization.details.tax.id.description')}</CaptionBold>
					</div>
				</div>
			}
			hideDialog={hideDialog}
			rightSideButtons={[
				{
					text: translate('cancel'),
					color: 'tertiary-light',
					onClick: hideDialog,
				},
				{
					text: translate('save'),
					color: 'primary-light',
					disabled: saveButtonDisabled,
					onClick: handleSubmit,
				},
			]}
		/>
	);
};

export default OrganizationTaxIdDialog;
